import type { ReactNode } from "react";

// Text-only "+ label" affordance. The canonical visual for "add another" /
// "add new field" triggers anywhere in the app — no chrome, no shape, just
// styled text in the document flow. Multiple PlusButtons can sit side by
// side without competing because there's nothing to compete with.
//
// `glyph` defaults to "+" but accepts any ReactNode so a caller can swap
// to "↗" / "—" / icon for a different verb. Override sparingly — most
// add-affordances should read as "+".
//
// `className` is composed onto the canonical recipe. Use it for layout
// concerns only (e.g. "mt-3" when this is the only element under a list).
// Don't override the typography or colour tokens — that's what the
// recipe is for.

interface PlusButtonProps {
  label: ReactNode;
  onClick: () => void;
  glyph?: ReactNode;
  disabled?: boolean;
  className?: string;
  title?: string;
  ariaLabel?: string;
}

export function PlusButton({
  label,
  onClick,
  glyph = "+",
  disabled = false,
  className = "",
  title,
  ariaLabel,
}: PlusButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={ariaLabel}
      className={`text-[11px] uppercase tracking-[0.15em] text-ink/55 transition hover:text-ink disabled:opacity-40 ${className}`}
    >
      {glyph} {label}
    </button>
  );
}
